import { Outlet } from "react-router-dom";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { User, LogOut, Settings, Bell, Menu as MenuIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth-context";
import { useUIStore } from "@/lib/store";
import Sidebar from "./Sidebar";
import SearchBar from "./SearchBar";

export default function Layout() {
  const { user, logout } = useAuth();
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const { i18n } = useTranslation();

  return (
    <div className="flex h-screen overflow-hidden bg-warmgray-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 sm:hidden"
          onClick={() => toggleSidebar()}
        />
      )}

      {/* Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-y-0 left-0 z-40 sm:relative sm:z-auto">
          <Sidebar />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="flex h-16 items-center justify-between border-b border-warmgray-200 bg-white px-4 shadow-sm">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSidebar}
              className="rounded-lg p-2 text-warmgray-500 hover:bg-warmgray-100 hover:text-warmgray-700"
            >
              <MenuIcon className="h-5 w-5" />
            </button>
            <SearchBar />
          </div>

          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <div className="flex items-center gap-1 rounded-lg border border-warmgray-200 p-0.5">
              <button
                onClick={() => i18n.changeLanguage("en")}
                className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                  i18n.language?.startsWith("en")
                    ? "bg-bark-100 text-bark-700"
                    : "text-warmgray-500 hover:text-warmgray-700"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => i18n.changeLanguage("lv")}
                className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                  i18n.language?.startsWith("lv")
                    ? "bg-bark-100 text-bark-700"
                    : "text-warmgray-500 hover:text-warmgray-700"
                }`}
              >
                LV
              </button>
            </div>

            {/* Notifications */}
            <button className="relative rounded-lg p-2 text-warmgray-500 hover:bg-warmgray-100 hover:text-warmgray-700">
              <Bell className="h-5 w-5" />
              <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-bark-500" />
            </button>

            {/* User Menu */}
            <Menu as="div" className="relative">
              <MenuButton className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-warmgray-100">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-bark-100 text-bark-600">
                  {user?.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt=""
                      className="h-8 w-8 rounded-full"
                    />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                </div>
                <span className="hidden text-sm font-medium text-warmgray-700 md:block">
                  {user?.displayName || user?.email || "User"}
                </span>
              </MenuButton>
              <MenuItems className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-warmgray-200 bg-white py-1 shadow-lg">
                <MenuItem>
                  {({ focus }) => (
                    <button
                      className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${
                        focus
                          ? "bg-warmgray-50 text-warmgray-900"
                          : "text-warmgray-700"
                      }`}
                    >
                      <Settings className="h-4 w-4" />
                      Account Settings
                    </button>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ focus }) => (
                    <button
                      onClick={logout}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${
                        focus
                          ? "bg-warmgray-50 text-warmgray-900"
                          : "text-warmgray-700"
                      }`}
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  )}
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
